import { useState, useMemo } from 'react'
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined'
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined'
import Table from '../common/table/Table'
import TableCell from '../common/table/TableCell'
import {
    activityTypeLabels,
    activitySubtypeLabels,
    deliveryModeLabels,
} from '../../helpers/dataHelpers/activityCategories'

const Chevron = ({ open }) =>
    open ? <KeyboardArrowUpOutlinedIcon fontSize='small' /> : <KeyboardArrowDownOutlinedIcon fontSize='small' />

const toggleInSet = (set, key) => {
    const next = new Set(set)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    return next
}

const COLUMNS = [
    { key: 'label', label: '', searchable: false, headerClassName: 'bd-acttab-head-label' },
    { key: 'pct', label: 'Inzet bij koplopers', searchable: false, headerClassName: 'bd-acttab-head-pct' },
]

const itemKeyHandler = (item) =>
    item.kind === 'delivery'
        ? `delivery-${item.subtypeKey}-${item.key}`
        : `${item.kind}-${item.key}`

const BenchmarkActivitiesSection = ({ activitiesOverview, showCallerHighlight = true }) => {
    const [expandedTypes, setExpandedTypes] = useState(() => new Set())
    const [expandedSubtypes, setExpandedSubtypes] = useState(() => new Set())

    const flattenedRows = useMemo(() => {
        if (!activitiesOverview) return []
        const { activityType, perType = {} } = activitiesOverview
        const rows = activityType?.rows || []
        const out = []
        rows.forEach(row => {
            out.push({ kind: 'type', key: row.key, topPct: row.topPct })
            if (!expandedTypes.has(row.key)) return

            const subtypes = perType[row.key]?.subtypes || []
            if (subtypes.length === 0) {
                out.push({ kind: 'empty', key: `empty-subtypes-${row.key}`, message: 'Geen subtypes geregistreerd.' })
                return
            }
            subtypes.forEach(subtype => {
                out.push({ kind: 'subtype', key: subtype.key, topPct: subtype.topPct })
                if (!expandedSubtypes.has(subtype.key)) return

                const deliveries = subtype.deliveryModes || []
                if (deliveries.length === 0) {
                    out.push({ kind: 'empty', key: `empty-delivery-${subtype.key}`, message: 'Geen uitvoeringsvormen geregistreerd.' })
                    return
                }
                deliveries.forEach(dm => {
                    out.push({ kind: 'delivery', subtypeKey: subtype.key, key: dm.key, topPct: dm.topPct })
                })
            })
        })
        return out
    }, [activitiesOverview, expandedTypes, expandedSubtypes])

    if (!activitiesOverview) return null

    const {
        activityType,
        callerActivityTypes = [],
        callerActivitySubtypes = [],
        callerSubtypeDeliveryPairs = [],
    } = activitiesOverview
    const rows = activityType?.rows || []
    if (rows.length === 0) return null

    const typeCallerSet     = showCallerHighlight ? new Set(callerActivityTypes)        : new Set()
    const subtypeCallerSet  = showCallerHighlight ? new Set(callerActivitySubtypes)     : new Set()
    const deliveryCallerSet = showCallerHighlight ? new Set(callerSubtypeDeliveryPairs) : new Set()

    const toggleTypeHandler = (key) => setExpandedTypes(prev => toggleInSet(prev, key))
    const toggleSubtypeHandler = (key) => setExpandedSubtypes(prev => toggleInSet(prev, key))

    const rowClassNameHandler = (item) => {
        if (item.kind === 'type') {
            const isOpen = expandedTypes.has(item.key)
            const isCaller = typeCallerSet.has(item.key)
            return `bd-acttab-row bd-acttab-row--type${isCaller ? ' bd-acttab-row--caller' : ''}${isOpen ? ' bd-acttab-row--open' : ''}`
        }
        if (item.kind === 'subtype') {
            const isOpen = expandedSubtypes.has(item.key)
            const isCaller = subtypeCallerSet.has(item.key)
            return `bd-acttab-row bd-acttab-row--subtype${isCaller ? ' bd-acttab-row--caller' : ''}${isOpen ? ' bd-acttab-row--open' : ''}`
        }
        if (item.kind === 'delivery') {
            const isCaller = deliveryCallerSet.has(`${item.subtypeKey}::${item.key}`)
            return `bd-acttab-row bd-acttab-row--delivery${isCaller ? ' bd-acttab-row--caller' : ''}`
        }
        return 'bd-acttab-row bd-acttab-row--empty'
    }

    const rowPropsHandler = (item) => {
        if (item.kind === 'type') {
            return {
                'data-testid': `bd-acttab-row-type-${item.key}`,
                'data-row-key': item.key,
                onClick: () => toggleTypeHandler(item.key),
            }
        }
        if (item.kind === 'subtype') {
            return {
                'data-testid': `bd-acttab-row-subtype-${item.key}`,
                'data-row-key': item.key,
                onClick: () => toggleSubtypeHandler(item.key),
            }
        }
        if (item.kind === 'delivery') {
            return {
                'data-testid': `bd-acttab-row-delivery-${item.subtypeKey}-${item.key}`,
                'data-row-key': item.key,
            }
        }
        return {}
    }

    const renderRowHandler = (item) => {
        if (item.kind === 'type') {
            const isOpen = expandedTypes.has(item.key)
            const label = activityTypeLabels[item.key] || item.key
            return (
                <>
                    <TableCell className='bd-acttab-cell-label'>
                        <Chevron open={isOpen} />
                        <span>{label}</span>
                    </TableCell>
                    <TableCell className='bd-acttab-cell-pct'>{item.topPct}%</TableCell>
                </>
            )
        }
        if (item.kind === 'subtype') {
            const isOpen = expandedSubtypes.has(item.key)
            const label = activitySubtypeLabels[item.key] || item.key
            return (
                <>
                    <TableCell className='bd-acttab-cell-label bd-acttab-cell-label--subtype'>
                        <Chevron open={isOpen} />
                        <span>{label}</span>
                    </TableCell>
                    <TableCell className='bd-acttab-cell-pct'>{item.topPct}%</TableCell>
                </>
            )
        }
        if (item.kind === 'delivery') {
            const label = deliveryModeLabels[item.key] || item.key
            return (
                <>
                    <TableCell className='bd-acttab-cell-label bd-acttab-cell-label--delivery'>
                        <span>{label}</span>
                    </TableCell>
                    <TableCell className='bd-acttab-cell-pct'>{item.topPct}%</TableCell>
                </>
            )
        }
        return (
            <TableCell colSpan={2} className='bd-acttab-cell-empty'>{item.message}</TableCell>
        )
    }

    return (
        <div className='bd-acttab-block'>
            <h2 className='bd-section-heading'>Activiteiten</h2>
            <p className='bd-section-sub'>
                Wat doen de organisaties die boven het benchmark presteren? Klik een hoofdtype open voor
                subtypes en uitvoeringsvormen.
            </p>
            <Table
                columns={COLUMNS}
                items={flattenedRows}
                renderRow={renderRowHandler}
                getItemKey={itemKeyHandler}
                getRowClassName={rowClassNameHandler}
                rowProps={rowPropsHandler}
                showSearch={false}
                tableClassName='bd-acttab'
            />
        </div>
    )
}

export default BenchmarkActivitiesSection
